/**
 * UI pieces: icons, top bar, pixel gauge (the signature), colour puck and
 * palette, draw tray, playback timeline, report dialog, avatar editor,
 * badge stickers, toast.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { PALETTE, REPORT_REASONS, addDays, base64ToBytes, bytesToBase64, dayKey, paletteHex, type EarnedBadge, type InkState, type ReportReason, type TileRegion } from '@drawearth/shared';
import { appStore, useApp } from '../lib/store.ts';
import { setThemeSetting, useTheme, type ThemeSetting } from '../lib/theme.ts';

// --- Icons (hand-drawn, 24-unit grid) --------------------------------------

type IconName = 'globe' | 'map' | 'bell' | 'person' | 'undo' | 'locate' | 'play' | 'flag' | 'brush' | 'close' | 'check' | 'spark' | 'sun' | 'moon' | 'menu';

const PATHS: Record<IconName, ReactNode> = {
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c3 3.5 3 14.5 0 18M12 3c-3 3.5-3 14.5 0 18" />
    </>
  ),
  map: <path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2zM9 4v14M15 6v14" />,
  bell: <path d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4zM10 21h4" />,
  person: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
    </>
  ),
  undo: <path d="M9 14L4 9l5-5M4 9h9a7 7 0 0 1 0 14h-3" />,
  locate: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
    </>
  ),
  play: <path d="M7 4l13 8-13 8z" />,
  flag: <path d="M5 21V4h11l-2 4 2 4H5" />,
  brush: <path d="M4 20c3 0 4-1 4-4 0-2 2-3 3-2 1 1 0 3-2 3 0 2-2 3-5 3zM11 13l8-8 2 2-8 8" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  check: <path d="M4 12l5 5L20 7" />,
  spark: <path d="M12 3l2 6 6 2-6 2-2 6-2-6-6-2 6-2z" />,
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>
  ),
  moon: <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z" />,
};

export function Icon({ name, size = 18 }: { name: IconName; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {PATHS[name]}
    </svg>
  );
}

// --- Top bar -----------------------------------------------------------------

/**
 * The bar carries the brand and the actions for the page. Its links sit inline
 * on a wide screen and collapse behind a burger on a phone, where the actions
 * need the room.
 */
export function TopBar({ right }: { right?: ReactNode }) {
  const signedIn = useApp((s) => s.signedIn);
  const unread = useApp((s) => s.unread);
  const [open, setOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onDown = (e: PointerEvent) => {
      if (!navRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);

  const links = (
    <>
      <NavLink to="/map" onClick={() => setOpen(false)}>
        <Icon name="map" />
        <span className="label">Map</span>
      </NavLink>
      {signedIn ? (
        <>
          <NavLink to="/notifications" aria-label="Notifications" onClick={() => setOpen(false)}>
            <Icon name="bell" />
            <span className="label">Activity</span>
            {unread > 0 && <span className="badge-count">{unread}</span>}
          </NavLink>
          <NavLink to="/profile" onClick={() => setOpen(false)}>
            <Icon name="person" />
            <span className="label">You</span>
          </NavLink>
        </>
      ) : (
        <NavLink to="/signin" onClick={() => setOpen(false)}>
          <Icon name="person" />
          <span className="label">Sign in</span>
        </NavLink>
      )}
    </>
  );

  return (
    <header className="topbar" data-testid="nav">
      <nav className={`pill-nav ${open ? 'is-open' : ''}`} aria-label="Main" ref={navRef}>
        <Link to="/" className="brand" onClick={() => setOpen(false)}>
          <Icon name="globe" size={20} />
          <span className="label">Draw Earth</span>
        </Link>
        <div className="pill-nav__links">{links}</div>
        <button
          type="button"
          className="pill-nav__burger"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls="main-menu"
          onClick={() => setOpen((v) => !v)}
          data-testid="menu-btn"
        >
          <Icon name={open ? 'close' : 'menu'} />
        </button>
        {open && (
          <div className="pill-nav__menu" id="main-menu" data-testid="menu">
            {links}
          </div>
        )}
      </nav>
      <div className="topbar__right">{right}</div>
    </header>
  );
}

// --- Theme -----------------------------------------------------------------------

/** One-tap light/dark switch (sets an explicit theme; Settings offers "system"). */
export function ThemeToggle() {
  const { theme } = useTheme();
  const next = theme === 'dark' ? 'light' : 'dark';
  return (
    <button type="button" className="btn btn--paper btn--icon" onClick={() => setThemeSetting(next)} aria-label={`Switch to ${next} theme`} title={`Switch to ${next} theme`} data-testid="theme-toggle">
      <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
    </button>
  );
}

const THEME_LABELS: Array<[ThemeSetting, string]> = [
  ['system', 'Match device'],
  ['light', 'Light'],
  ['dark', 'Dark'],
];

export function ThemeChips() {
  const { setting } = useTheme();
  return (
    <div className="chips" role="radiogroup" aria-label="Theme">
      {THEME_LABELS.map(([v, label]) => (
        <button key={v} type="button" role="radio" aria-checked={setting === v} className="chip" onClick={() => setThemeSetting(v)} data-testid={`theme-${v}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

// --- Pixel gauge (signature) --------------------------------------------------

export function PixelGauge({ value, max, blocks = 10, label, wide = false, night = false, frozen = false }: { value: number; max: number; blocks?: number; label: string; wide?: boolean; night?: boolean; frozen?: boolean }) {
  const filled = max > 0 ? Math.min(blocks, Math.round((value / max) * blocks)) : 0;
  const prev = useRef(filled);
  const [fresh, setFresh] = useState<number | null>(null);
  useEffect(() => {
    if (filled > prev.current) {
      setFresh(filled - 1);
      const t = setTimeout(() => setFresh(null), 450);
      prev.current = filled;
      return () => clearTimeout(t);
    }
    prev.current = filled;
  }, [filled]);
  return (
    <div className={`gauge ${wide ? 'gauge--wide' : ''} ${night ? 'gauge--night' : ''}`} role="meter" aria-valuenow={value} aria-valuemin={0} aria-valuemax={max} aria-label={label} data-testid="ink">
      <div className="gauge__blocks" aria-hidden="true">
        {Array.from({ length: blocks }, (_, i) => (
          <span key={i} className={`gauge__block ${i < filled ? 'is-on' : ''} ${i === fresh ? 'is-new' : ''} ${frozen && i < filled ? 'is-frozen' : ''}`} />
        ))}
      </div>
      <span className="gauge__label">{label}</span>
    </div>
  );
}

export function InkGauge({ ink, wide = false, night = false }: { ink: InkState | null; wide?: boolean; night?: boolean }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  if (!ink) return null;
  const next = ink.nextAt ? Math.max(0, Math.ceil((ink.nextAt - Date.now()) / 1000)) : null;
  const label = ink.frozenUntil ? `${ink.ink}/${ink.cap} ink · frozen` : `${ink.ink}/${ink.cap} ink${next !== null && wide ? ` · +1 in ${next}s` : ''}`;
  return <PixelGauge value={ink.ink} max={ink.cap} blocks={wide ? 20 : 10} label={label} wide={wide} night={night} frozen={!!ink.frozenUntil} />;
}

// --- Colour puck & palette ----------------------------------------------------

export function PaletteGrid({ value, onChange }: { value: number; onChange: (c: number) => void }) {
  return (
    <div className="palette__grid" role="listbox" aria-label="Palette">
      {PALETTE.map((_, i) =>
        i === 0 ? null : (
          <button key={i} type="button" role="option" aria-selected={i === value} className={`palette__swatch ${i === value ? 'is-selected' : ''}`} style={{ background: paletteHex(i) }} title={`Colour ${i}`} onClick={() => onChange(i)} />
        ),
      )}
    </div>
  );
}

export function ColourPuck({ value, onChange }: { value: number; onChange: (c: number) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="palette" data-testid="palette">
      <button type="button" className="puck" style={{ background: paletteHex(value) }} onClick={() => setOpen((o) => !o)} aria-label="Choose a colour" aria-expanded={open} />
      {open && (
        <div className="palette__sheet">
          <PaletteGrid
            value={value}
            onChange={(c) => {
              onChange(c);
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

// --- Draw tray -----------------------------------------------------------------

export function DrawTray({ canUndo, onUndo, onExit, ink, status, error, live, onLive }: { canUndo: boolean; onUndo: () => void; onExit: () => void; ink: InkState | null; status: string | null; error: boolean; live: boolean; onLive: (v: boolean) => void }) {
  const colour = useApp((s) => s.colour);
  return (
    <div className="tray" data-testid="draw-toolbar">
      <div className="tray__row">
        <ColourPuck value={colour} onChange={(c) => appStore.set({ colour: c })} />
        <InkGauge ink={ink} />
        <span className="tray__spacer" />
        <button type="button" className={`btn btn--icon ${live ? 'btn--live' : 'btn--paper'}`} onClick={() => onLive(!live)} aria-pressed={live} aria-label={live ? 'You are shown live. Tap to hide.' : 'Show your avatar where you paint'} title={live ? 'Live: your avatar shows where you paint' : 'Go live'} data-testid="live-toggle">
          <Icon name="person" />
        </button>
        <button type="button" className="btn btn--paper btn--icon" disabled={!canUndo} onClick={onUndo} aria-label="Undo last pixel" data-testid="undo">
          <Icon name="undo" />
        </button>
        <button type="button" className="btn" onClick={onExit}>
          <Icon name="check" /> Done
        </button>
      </div>
      <div className={`tray__status ${error ? 'is-error' : ''}`}>{status}</div>
    </div>
  );
}

// --- Playback timeline ---------------------------------------------------------

export function PlaybackTray({ firstDate, dates, value, onChange, onExit, excludeAgents, onExcludeAgents }: { firstDate: string | null; dates: string[]; value: string; onChange: (d: string) => void; onExit: () => void; excludeAgents: boolean; onExcludeAgents: (v: boolean) => void }) {
  const today = dayKey(Date.now());
  const weekAgo = addDays(today, -7);
  const start = firstDate && firstDate < weekAgo ? firstDate : weekAgo;
  const days = useMemo(() => {
    const out: string[] = [];
    for (let d = start; d <= today; d = addDays(d, 1)) out.push(d);
    return out.length ? out : [today];
  }, [start, today]);
  const index = Math.max(0, days.indexOf(value));
  const snapshots = new Set(dates);
  const ref = useRef<HTMLDivElement>(null);
  const pick = (clientX: number): void => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const i = Math.max(0, Math.min(days.length - 1, Math.floor(((clientX - r.left) / r.width) * days.length)));
    if (days[i] !== value) onChange(days[i]);
  };
  const pretty = new Date(value + 'T00:00:00Z').toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
  return (
    <div className="tray" data-testid="playback">
      <div className="tray__row">
        <button type="button" className="btn btn--paper btn--icon" onClick={onExit} aria-label="Exit playback">
          <Icon name="close" />
        </button>
        <div
          className="timeline"
          ref={ref}
          onPointerDown={(e) => {
            (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
            pick(e.clientX);
          }}
          onPointerMove={(e) => {
            if (e.buttons & 1) pick(e.clientX);
          }}
        >
          {days.map((d, i) => (
            <span key={d} className={`timeline__day ${snapshots.has(d) ? 'has-snapshot' : ''} ${d === today ? 'is-today' : ''} ${i === index ? 'is-selected' : ''}`} />
          ))}
          <input type="range" min={0} max={days.length - 1} value={index} onChange={(e) => onChange(days[Number(e.target.value)])} aria-label="Playback day" data-testid="playback-range" />
        </div>
        <span className="timeline__label">
          {pretty}
          {value === today ? ' · live' : snapshots.has(value) ? ' · snapshot' : ''}
        </span>
      </div>
      <div className="tray__row">
        <span className="tray__status">{days.length} days · drag to scrub</span>
        <span className="tray__spacer" />
        <label className="toggle">
          <input type="checkbox" checked={excludeAgents} onChange={(e) => onExcludeAgents(e.target.checked)} />
          <span className="toggle__track" />
          Hide agent paint
        </label>
      </div>
    </div>
  );
}

// --- Report dialog ------------------------------------------------------------

const REASON_LABELS: Record<ReportReason, string> = { hate: 'Hate', harassment: 'Harassment', sexual: 'Sexual content', personal_info: 'Personal info', other: 'Something else' };

export function ReportDialog({ tile, region, onSubmit, onCancel }: { tile: string; region: TileRegion; onSubmit: (reason: ReportReason, note: string) => Promise<void>; onCancel: () => void }) {
  const [reason, setReason] = useState<ReportReason>('other');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const w = region.i1 - region.i0 + 1;
  const h = region.j1 - region.j0 + 1;
  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="report-title" data-testid="report-dialog">
      <div className="modal__card">
        <h2 id="report-title">Report this patch</h2>
        <p className="pixel muted">
          {w} × {h} px · tile {tile}
        </p>
        <div className="chips" role="radiogroup" aria-label="Reason" data-testid="report-reason">
          {REPORT_REASONS.map((r) => (
            <button key={r} type="button" role="radio" aria-checked={reason === r} className="chip" onClick={() => setReason(r)} data-testid={`reason-${r}`}>
              {REASON_LABELS[r]}
            </button>
          ))}
        </div>
        <label>
          Anything else? (optional)
          <textarea value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="What should a moderator look at?" />
        </label>
        <div className="row">
          <button type="button" className="btn btn--paper" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--danger"
            disabled={busy}
            data-testid="report-submit"
            onClick={async () => {
              setBusy(true);
              try {
                await onSubmit(reason, note);
              } finally {
                setBusy(false);
              }
            }}
          >
            <Icon name="flag" /> Send report
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Avatar --------------------------------------------------------------------

export function AvatarEditor({ value, onSave, pixelCost }: { value: string | null; onSave: (b64: string) => Promise<void>; pixelCost: number }) {
  const [pixels, setPixels] = useState<Uint8Array>(() => (value ? base64ToBytes(value) : new Uint8Array(256)));
  const [busy, setBusy] = useState(false);
  const colour = useApp((s) => s.colour);
  const [tool, setTool] = useState<'paint' | 'erase'>('paint');
  const original = useMemo(() => (value ? base64ToBytes(value) : new Uint8Array(256)), [value]);
  const painting = useRef<number | null>(null);
  let changed = 0;
  for (let i = 0; i < 256; i++) if (pixels[i] !== original[i]) changed++;
  const filled = pixels.filter((v) => v !== 0).length;
  const set = (i: number, c: number): void => {
    setPixels((p) => {
      if (p[i] === c) return p;
      const next = new Uint8Array(p);
      next[i] = c;
      return next;
    });
  };
  return (
    <div className="avatar-editor" data-testid="avatar-editor">
      <div className="chips" role="radiogroup" aria-label="Tool">
        <button type="button" role="radio" aria-checked={tool === 'paint'} className="chip" onClick={() => setTool('paint')} data-testid="tool-paint">
          Paint
        </button>
        <button type="button" role="radio" aria-checked={tool === 'erase'} className="chip" onClick={() => setTool('erase')} data-testid="tool-erase">
          Erase
        </button>
        <button type="button" className="chip" onClick={() => setPixels(new Uint8Array(256))} data-testid="tool-clear">
          Clear
        </button>
      </div>
      <div
        className="avatar-grid"
        onContextMenu={(e) => e.preventDefault()}
        onPointerLeave={() => (painting.current = null)}
        onPointerUp={() => (painting.current = null)}
      >
        {Array.from(pixels).map((v, i) => (
          <button
            key={i}
            type="button"
            className="avatar-grid__px"
            style={{ background: v ? paletteHex(v) : '#fff' }}
            onPointerDown={(e) => {
              painting.current = e.button === 2 || tool === 'erase' ? 0 : colour;
              set(i, painting.current);
            }}
            onPointerEnter={() => {
              if (painting.current !== null) set(i, painting.current);
            }}
            aria-label={`avatar pixel ${i}`}
          />
        ))}
      </div>
      <div className="palette palette--inline">
        <PaletteGrid value={colour} onChange={(c) => appStore.set({ colour: c })} />
      </div>
      <p className="pixel muted">
        {filled}/256 filled · {changed} changed · {changed * pixelCost} ink
      </p>
      <div className="row">
        <button
          type="button"
          className="btn"
          disabled={busy || changed === 0}
          onClick={async () => {
            setBusy(true);
            try {
              await onSave(bytesToBase64(pixels));
            } finally {
              setBusy(false);
            }
          }}
        >
          <Icon name="check" /> Save avatar
        </button>
        <span className="muted">Drag to paint. Right-click erases.</span>
      </div>
    </div>
  );
}

const avatarUrlCache = new Map<string, string>();

/** 16×16 avatar as a data URL (cached), for many small images such as map callouts. */
export function avatarDataUrl(value: string | null): string | null {
  if (!value) return null;
  const hit = avatarUrlCache.get(value);
  if (hit) return hit;
  const px = base64ToBytes(value);
  const c = document.createElement('canvas');
  c.width = 16;
  c.height = 16;
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  const img = ctx.createImageData(16, 16);
  for (let i = 0; i < 256; i++) {
    const col = px[i];
    if (!col) continue;
    const [r, g, b] = PALETTE[col];
    img.data[i * 4] = r;
    img.data[i * 4 + 1] = g;
    img.data[i * 4 + 2] = b;
    img.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  const url = c.toDataURL();
  if (avatarUrlCache.size > 200) avatarUrlCache.clear();
  avatarUrlCache.set(value, url);
  return url;
}

export function AvatarImage({ value, size = 64 }: { value: string | null; size?: number }) {
  const px = value ? base64ToBytes(value) : null;
  return (
    <div className="avatar" style={{ width: size, height: size }} aria-label="avatar">
      {px && Array.from(px).map((v, i) => <span key={i} style={{ background: v ? paletteHex(v) : 'transparent' }} />)}
    </div>
  );
}

// --- Badges ---------------------------------------------------------------------

const CATEGORY_COLOUR: Record<string, string> = { firsts: 'var(--sun)', range: 'var(--sky)', volume: 'var(--leaf)', social: 'var(--coral)', meta: 'var(--grape)' };

/** A deterministic 5×5 symmetric pixel sigil for a badge key. */
export function sigilFor(key: string): boolean[] {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 16777619) >>> 0;
  const cells: boolean[] = [];
  for (let y = 0; y < 5; y++) {
    const row: boolean[] = [];
    for (let x = 0; x < 3; x++) {
      row.push(((h >>> ((y * 3 + x) % 31)) & 1) === 1);
    }
    cells.push(row[0], row[1], row[2], row[1], row[0]);
  }
  if (!cells.some(Boolean)) cells[12] = true;
  return cells;
}

export function Sigil({ badgeKey, category }: { badgeKey: string; category: string }) {
  const cells = sigilFor(badgeKey);
  const colour = CATEGORY_COLOUR[category] ?? 'var(--ink)';
  return (
    <div className="sigil" aria-hidden="true">
      {cells.map((on, i) => (
        <span key={i} style={{ background: on ? colour : 'transparent' }} />
      ))}
    </div>
  );
}

export function BadgeGrid({ badges }: { badges: EarnedBadge[] }) {
  if (badges.length === 0) return <div className="empty">No badges yet. Your first pixel earns one.</div>;
  return (
    <ul className="badges" data-testid="badges">
      {badges.map((b, i) => (
        <li key={b.key} className="sticker" title={b.description} style={{ animationDelay: `${i * 40}ms` }}>
          <Sigil badgeKey={b.key} category={b.category} />
          <div>
            <div className="sticker__name">{b.name}</div>
            <div className="sticker__date">{new Date(b.earnedAt).toLocaleDateString()}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

// --- Toast ---------------------------------------------------------------------

export function Toast() {
  const msg = useApp((s) => s.toast);
  if (!msg) return null;
  return (
    <div className="toast" role="status" data-testid="toast">
      {msg}
    </div>
  );
}
